/**
 * Helper pur qui encode la hiérarchie métier des permissions sur une
 * autorisation ( ticket #521 - réponse Damien Maurice 2026-05-25 ) .
 *
 * <p>Miroir TypeScript exact du backend
 * {@code OperationTypeHierarchy.java} - les deux sources DOIVENT rester
 * alignées . Toute divergence créerait un écart entre le payload émis
 * par l'UI et le payload normalisé en base , menant à des bugs subtils
 * de droits effectifs .
 *
 * <p><b>Règles métier ( hiérarchie stricte intra-ligne ) :</b>
 * <ul>
 *   <li>{@code depot} et {@code publication} = même opération métier
 *       ( miroir auto : présence de l'une = présence de l'autre ) .</li>
 *   <li>{@code delete} implique {@code depot + publication + extraction} .</li>
 *   <li>{@code depot} ( ou {@code publication} ) implique {@code extraction} .</li>
 *   <li>{@code extraction} seule reste seule .</li>
 *   <li>Autres types ( ex {@code associate} ) laissés tels quels .</li>
 * </ul>
 *
 * <p><b>Combinaisons valides côté UI 3 colonnes ( Suppression / Dépôt /
 * Extraction ) :</b> {@code []} , {@code [extraction]} ,
 * {@code [extraction, depot, publication]} ,
 * {@code [extraction, depot, publication, delete]} .
 *
 * <p>Toutes les fonctions sont pures : pas de side effect , pas de
 * dépendance Vue / Buefy / DOM . Entrées non mutées . Testables
 * unitairement sans mount .
 */

export const OPERATION_DELETE = "delete";
export const OPERATION_DEPOT = "depot";
export const OPERATION_PUBLICATION = "publication";
export const OPERATION_EXTRACTION = "extraction";

/**
 * Normalise un ensemble d'opérations en appliquant la hiérarchie stricte
 * et le miroir {@code depot <-> publication} . Renvoie un nouveau Set
 * ( ne mute pas l'entrée ) .
 */
export function normalize(input: Iterable<string> | null | undefined): Set<string> {
  const result = new Set<string>();
  if (!input) return result;
  for (const op of input) {
    if (op) result.add(op);
  }
  // delete implique depot + publication + extraction
  if (result.has(OPERATION_DELETE)) {
    result.add(OPERATION_DEPOT);
    result.add(OPERATION_PUBLICATION);
    result.add(OPERATION_EXTRACTION);
  }
  // miroir auto depot <-> publication ( même opération métier )
  if (result.has(OPERATION_DEPOT) || result.has(OPERATION_PUBLICATION)) {
    result.add(OPERATION_DEPOT);
    result.add(OPERATION_PUBLICATION);
    // depot implique extraction
    result.add(OPERATION_EXTRACTION);
  }
  return result;
}

/**
 * Retourne {@code true} si {@code key} est verrouillé par la hiérarchie
 * compte tenu de {@code currentOps} ( ne peut pas être décoché tant
 * qu'un droit racine de niveau supérieur est présent ) .
 *
 * <p>Cas verrouillés :
 * <ul>
 *   <li>{@code extraction} si {@code depot} , {@code publication} ou
 *       {@code delete} est présent .</li>
 *   <li>{@code depot} ou {@code publication} si {@code delete} est
 *       présent .</li>
 * </ul>
 */
export function isLockedByHierarchy(key: string, currentOps: Set<string>): boolean {
  if (key === OPERATION_EXTRACTION) {
    return currentOps.has(OPERATION_DEPOT)
      || currentOps.has(OPERATION_PUBLICATION)
      || currentOps.has(OPERATION_DELETE);
  }
  if (key === OPERATION_DEPOT || key === OPERATION_PUBLICATION) {
    return currentOps.has(OPERATION_DELETE);
  }
  return false;
}

/**
 * Applique le cochage d'une opération en cascadant les dépendances
 * descendantes . Pure : renvoie un nouveau Set .
 */
export function applyCheck(key: string, currentOps: Set<string>): Set<string> {
  const next = new Set(currentOps);
  next.add(key);
  return normalize(next);
}

/**
 * Applique le décochage d'une opération en respectant le miroir
 * {@code depot <-> publication} ( décocher l'un retire l'autre ) .
 * Pure : renvoie un nouveau Set .
 *
 * <p>Garde-fou : si {@code key} est verrouillé par la hiérarchie ,
 * renvoie le Set inchangé ( pas de décochage possible ) - le caller
 * peut détecter cela en comparant les tailles si besoin .
 */
export function applyUncheck(key: string, currentOps: Set<string>): Set<string> {
  if (isLockedByHierarchy(key, currentOps)) {
    return new Set(currentOps);
  }
  const next = new Set(currentOps);
  next.delete(key);
  // Miroir auto depot <-> publication : décocher l'un retire l'autre
  // pour éviter qu'un payload incohérent soit envoyé au backend , où la
  // normalisation ré-ajouterait alors le miroir et annulerait la
  // suppression .
  if (key === OPERATION_DEPOT) next.delete(OPERATION_PUBLICATION);
  else if (key === OPERATION_PUBLICATION) next.delete(OPERATION_DEPOT);
  return next;
}
